import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { auth, googleProvider } from './firebase';

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} Authentication result
 */
export async function signInWithEmail(email, password) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return {
      user: userCredential.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing in with email:', error);
    return {
      user: null,
      error: getAuthErrorMessage(error.code)
    };
  }
}

/**
 * Create a new user account with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} displayName - User display name
 * @returns {Promise<Object>} Authentication result
 */
export async function createUserWithEmail(email, password, displayName) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update the user's profile (display name and default avatar)
    await updateProfile(userCredential.user, {
      displayName: displayName || userCredential.user.displayName || email?.split('@')[0],
      photoURL: userCredential.user.photoURL || '/assets/images/no_image.png'
    });
    
    return {
      user: userCredential.user,
      error: null
    };
  } catch (error) {
    console.error('Error creating user with email:', error);
    return {
      user: null,
      error: getAuthErrorMessage(error.code)
    };
  }
}

/**
 * Sign in with Google using Firebase Auth
 * @returns {Promise<Object>} Authentication result
 */
export async function signInWithGoogle() {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    // Ensure a photoURL is set; if missing, apply default
    if (!result.user.photoURL) {
      try { await updateProfile(result.user, { photoURL: '/assets/images/no_image.png' }); } catch (_) {}
    }
    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing in with Google:', error);
    return {
      user: null,
      error: getAuthErrorMessage(error.code)
    };
  }
}

/**
 * Sign out the current user
 * @returns {Promise<Object>} Sign out result
 */
export async function signOut() {
  try {
    await firebaseSignOut(auth);
    return { error: null };
  } catch (error) {
    console.error('Error signing out:', error);
    return { error: error.message };
  }
}

/**
 * Get the current user
 * @returns {Object|null} Current user or null
 */
export function getCurrentUser() {
  return auth.currentUser;
}

/**
 * Listen for authentication state changes
 * @param {Function} callback - Callback function to handle auth state changes
 * @returns {Function} Unsubscribe function
 */
export function onAuthStateChange(callback) {
  return onAuthStateChanged(auth, callback);
}

/**
 * Convert Firebase auth error codes to user-friendly messages
 * @param {string} errorCode - Firebase error code
 * @returns {string} User-friendly error message
 */
function getAuthErrorMessage(errorCode) {
  const errorMessages = {
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password should be at least 6 characters long.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',
    'auth/popup-blocked': 'Popup was blocked by your browser. Please allow popups and try again.'
  };

  return errorMessages[errorCode] || 'An error occurred during authentication. Please try again.';
}
